import React from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Building2, FileText, CheckCircle2, PrinterCheck, MailCheck, Globe, Truck, CalendarClock, Receipt } from "lucide-react";
import { IconButton } from "../../../components/ButtonComponents";
import { useAuthStore } from "../../../store/authStore";

interface DespatchViewDialogProps {
    invoice: any; // DespatchList'te invoice prop'u üzerinden gönderildiği için adını koruduk
    isOpen: boolean;
    onClose: () => void;
    onConvertToInvoice?: (despatch: any) => void;
}

export const DespatchViewDialog: React.FC<DespatchViewDialogProps> = ({ invoice: despatch, isOpen, onClose, onConvertToInvoice }) => {
    const { user } = useAuthStore();
    if (!despatch) return null;

    // direction === 'OUT' (Bizden çıkış) ise Sevk İrsaliyesi, 'IN' ise Giriş İrsaliyesi
    const isSevk = despatch.direction === 'OUT'; 
    const currency = despatch.currency || 'TRY';
    const exchangeRate = despatch.exchangeRate || 1;
    const isForeign = currency !== 'TRY';

    const header = (
        <div className="flex justify-between items-center w-full pr-8">
            <div className="flex items-center gap-2 text-[var(--text)]">
                <FileText size={20} />
                <span className="text-lg font-bold">İrsaliye Detayı: {despatch.number || '#NO-REF'}</span>
            </div>
            <div className="flex gap-2">
               {!despatch.invoiceId && onConvertToInvoice && (
                   <IconButton icon={Receipt} onClick={() => { onClose(); onConvertToInvoice(despatch); }} title="Faturaya Çevir" className="text-green-600 hover:bg-green-50" />
               )}
               <IconButton icon={PrinterCheck} onClick={() => window.print()} title="Yazdır" className="text-[var(--text)] hover:bg-[var(--bg-alt)]" /> 
               <IconButton icon={MailCheck} title="E-Posta Gönder" className="text-[var(--primary)] hover:bg-[var(--primary)]/10" />  
            </div>
        </div>
    );

    return (
        <Dialog  
            header={header}
            visible={isOpen} 
            onHide={onClose} 
            maximized
            modal 
            className="invoice-view-dialog"
        >
            <div className="max-w-5xl mx-auto">
                {/* Renkli Şerit: Sevk için kehribar(amber), Giriş için mavi */}
                <div className={`h-2 rounded-t-xl ${isSevk ? 'bg-[var(--secondary)]' : 'bg-[var(--primary)]'}`} />
                
                <div className="shadow-lg rounded-t-none card bg-[var(--card)] border border-[var(--border)] overflow-hidden print:shadow-none print:border-none">
                    <div className="p-4 sm:p-6 md:p-8">
                        {/* Logo ve Belge Başlığı */}
                        <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-8">
                            <div className="flex gap-4 items-center">
                                <div className={`p-3 rounded-xl ${isSevk ? 'bg-[var(--secondary)]/10 text-[var(--secondary)]' : 'bg-[var(--primary)]/10 text-[var(--primary)]'}`}>
                                    <Building2 size={32} />
                                </div>
                                <div>
                                    <h2 className="text-xl font-black tracking-tight uppercase text-[var(--text)]">{user?.firmName! || 'FİRMA ADI'}</h2>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text)] opacity-60">Resmi İrsaliye Belgesi</p>
                                </div>
                            </div>
                            <div className="text-left sm:text-right">
                                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full font-bold text-[10px] mb-2 ${isSevk ? 'bg-[var(--secondary)]/10 text-[var(--secondary)]' : 'bg-[var(--primary)]/10 text-[var(--primary)]'}`}>
                                    <CheckCircle2 size={12} /> {isSevk ? 'SEVK İRSALİYESİ' : 'GİRİŞ İRSALİYESİ'}
                                </div>
                                <h3 className="text-2xl font-mono tracking-tighter block font-bold text-[var(--text)]">
                                    {despatch.number || '#NO-REF'}
                                </h3>
                            </div>
                        </div>

                        {/* Bilgi Kartları Grubu */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            {/* Sol Taraf: Müşteri ve Lojistik */}
                            <div className="flex flex-col gap-4">
                                <div className="card2 p-4 border border-[var(--border)] rounded-xl bg-[var(--bg-alt)]/50">
                                    <label className="text-[10px] font-black uppercase tracking-widest block mb-3 text-[var(--text)] opacity-60">
                                        Müşteri / Cari Bilgileri
                                    </label>
                                    <h4 className="text-lg font-bold mb-1 text-[var(--text)]">{despatch.account?.name}</h4>
                                    <div className="text-sm space-y-1 text-[var(--text)] opacity-80">
                                        <p><span className="font-medium">Vergi No:</span> {despatch.account?.taxNumber || '-'}</p>
                                        <p><span className="font-medium">E-posta:</span> {despatch.account?.email || '-'}</p>
                                        <p><span className="font-medium">Telefon:</span> {despatch.account?.phone || '-'}</p>
                                    </div>
                                </div>

                                {/* Taşıyıcı Bilgileri Paneli */}
                                {(despatch.transporterName || despatch.transporterTcknVkn) && (
                                    <div className="card2 p-4 border border-[var(--border)] rounded-xl bg-[var(--bg-alt)]/50 flex flex-col gap-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-[var(--text)] opacity-60 flex items-center gap-1">
                                            <Truck size={12}/> Taşıyıcı / Lojistik Bilgileri
                                        </label>
                                        <div className="flex justify-between items-center text-sm text-[var(--text)]">
                                            <span className="font-medium opacity-80">Firma / Şoför:</span>
                                            <span className="font-bold">{despatch.transporterName || '-'}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-sm text-[var(--text)]">
                                            <span className="font-medium opacity-80">TCKN / VKN:</span>
                                            <span className="font-bold font-mono">{despatch.transporterTcknVkn || '-'}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            {/* Sağ Taraf: Tarihler ve Durum */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 h-fit">
                                <div className="card2 p-4 border border-[var(--border)] rounded-xl bg-[var(--card)] shadow-sm">
                                    <label className="text-[9px] font-bold uppercase block mb-1 text-[var(--text)] opacity-60">Düzenleme Tarihi</label>
                                    <span className="text-sm font-bold text-[var(--text)]">
                                        {new Date(despatch.date).toLocaleDateString('tr-TR')}
                                    </span>
                                </div>
                                <div className="card2 p-4 border border-[var(--border)] rounded-xl bg-[var(--secondary)]/5 shadow-sm">
                                    <label className="text-[9px] font-bold uppercase block mb-1 text-[var(--secondary)] flex items-center gap-1">
                                        <CalendarClock size={10}/> Fiili Sevk Tarihi
                                    </label>
                                    <span className="text-sm font-bold text-[var(--text)]">
                                        {despatch.transportDate 
                                            ? new Date(despatch.transportDate).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) 
                                            : new Date(despatch.date).toLocaleDateString('tr-TR')}
                                    </span>
                                </div>
                                <div className="card2 p-4 border border-[var(--border)] rounded-xl bg-[var(--card)] shadow-sm">
                                    <label className="text-[9px] font-bold uppercase block mb-1 text-[var(--text)] opacity-60">Belge Türü</label>
                                    <span className="text-[10px] font-black text-[var(--primary)] uppercase">
                                        {despatch.eInvoiceProfile === "TEMELIRSALIYE" ? "E-İRSALİYE" : "MATBU İRSALİYE"}
                                    </span>
                                </div>
                                <div className="card2 p-4 border border-[var(--border)] rounded-xl bg-[var(--card)] shadow-sm">
                                    <label className="text-[9px] font-bold uppercase block mb-1 text-[var(--text)] opacity-60">Depo</label>
                                    <span className="text-[11px] font-bold text-[var(--text)] line-clamp-2">
                                        {despatch.stockMovements?.[0]?.warehouse?.name || 'Belirtilmedi'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Sevk Kalemleri Tablosu */}
                        <div className="mb-8 border border-[var(--border)] rounded-xl overflow-hidden shadow-sm print:shadow-none print:border-gray-300 bg-[var(--card)]">
                            <DataTable 
                                value={despatch.stockMovements} 
                                className="p-datatable-sm"
                                rowHover
                                responsiveLayout="scroll"
                            >
                                <Column header="#" body={(_, opt) => opt.rowIndex + 1} style={{ width: '3rem', textAlign: 'center' }} />
                                <Column header="ÜRÜN / VARYANT" body={(row) => (
                                    <div className="flex flex-col py-1">
                                        <span className="font-bold text-[var(--text)]">{row.stock?.name}</span>
                                        {row.variant?.name && (
                                            <span className="text-[9px] bg-[var(--bg-alt)] text-[var(--text)] opacity-80 px-1.5 py-0.5 rounded border border-[var(--border)] w-fit font-bold uppercase mt-1">
                                                {row.variant.name}
                                            </span>
                                        )}
                                    </div>
                                )} />
                                <Column header="SEVK MİKTARI" body={(row) => <span className="font-black text-[var(--primary)] text-md">{row.quantity}</span>} align="center" style={{ width: '10rem' }} />
                                <Column 
                                    header="BİRİM FİYAT" 
                                    body={(row) => <span className="text-[var(--text)] opacity-80 font-medium">{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency }).format(row.unitPrice)}</span>} 
                                    align="right" 
                                />
                                <Column header="KDV" body={(row) => <span className="text-[var(--text)] opacity-80">{`%${row.taxRate}`}</span>} align="center" />
                                <Column 
                                    header="TOPLAM TUTAR" 
                                    body={(row) => (
                                        <span className="font-bold text-[var(--text)]">
                                            {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency }).format(row.totalAmount)}
                                        </span>
                                    )} 
                                    align="right" 
                                />
                            </DataTable>
                        </div>

                        {/* Alt Notlar ve Toplamlar */}
                        <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-end gap-8 mt-auto">
                            <div className="flex-1 w-full p-4 rounded-xl border border-dashed border-[var(--border)] bg-[var(--bg-alt)]/50">
                                <label className="text-[10px] font-bold uppercase text-[var(--text)] opacity-60 flex items-center gap-1 mb-2">
                                    <Globe size={10}/> Açıklama / Notlar
                                </label>
                                <p className="text-xs italic leading-relaxed text-[var(--text)] opacity-80 font-medium">
                                    {despatch.description || "İrsaliye belgesine ait özel bir not bulunmamaktadır."}
                                </p>
                            </div>
                            
                            <div className="w-full lg:w-96 space-y-3 p-4 rounded-xl border border-[var(--border)] bg-[var(--card)] shadow-sm">
                                <div className="flex justify-between px-2 text-sm text-[var(--text)] opacity-80">
                                    <span className="font-medium">Ara Toplam</span>
                                    <span className="font-bold">{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency }).format(despatch.baseAmount)}</span>
                                </div>
                                <div className="flex justify-between px-2 text-sm text-[var(--text)] opacity-80">
                                    <span className="font-medium">Toplam KDV</span>
                                    <span className="font-bold">{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency }).format(despatch.taxAmount)}</span>
                                </div>
                                {despatch.discountAmount > 0 && (
                                    <div className="flex justify-between px-2 text-sm text-[var(--text)] opacity-80">
                                        <span className="font-medium">İskonto</span>
                                        <span className="font-bold text-[var(--danger)]">-{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency }).format(despatch.discountAmount)}</span>
                                    </div>
                                )}
                                
                                <div className="flex justify-between items-center border-t border-[var(--border)] pt-3 mt-3 text-[var(--text)]">
                                    <div className="flex flex-col">
                                        <span className="text-[10px] font-black uppercase tracking-widest opacity-60">Genel Toplam</span>
                                        {isForeign && (
                                            <span className="text-[9px] font-bold opacity-40">
                                                TL Karşılığı: {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(despatch.amount * exchangeRate)}
                                            </span>
                                        )}
                                    </div>
                                    <span className={`text-2xl font-black italic text-[var(--primary)]`}>
                                        {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency }).format(despatch.amount)}
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="mt-8 text-center print:block hidden">
                            <p className="text-[10px] text-[var(--text)] opacity-40 font-medium">Bu belge elektronik ortamda oluşturulmuştur.</p>
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};